<template>
  <div class="package-card">
    <div class="thumbnail-wrapper">
      <img :src="coverImage" class="thumbnail" :class="{ minified: isMinified, 'with-avatar': packageType === DataType.OfferedPackages }" @error="onCoverImageError">
      <div v-if="orientations && orientations.length > 0" class="orientation-tags">
        <VideoLayoutOrientation
          v-for="orientation in orientations"
          :key="orientation"
          class="layout-orientation"
          :is-vertical="orientation === Orientation.Vertical"
          :size="TagSizes.Small"
        />
      </div>
    </div>
    <div class="dropdown-actions">
      <ActionButtons v-if="dropdownActions" :actions="dropdownActions" :view-type="DataViewType.Grid" is-grey-icon />
    </div>
    <div class="creator-wrapper" :class="{ minified: isMinified }">
      <ProfileImage
        v-if="packageType === DataType.OfferedPackages"
        :profile-image="profileImage"
        :fallback-profile-image="fallbackProfileImage"
        custom-width-height="48px"
        custom-outline="2px solid white"
      />
    </div>
    <div class="creator-wrapper-title">
      <NewTooltip v-if="isMinified" class="title" :content="packageName" auto-ellipsis />
      <NewTooltip v-else-if="packageType === DataType.OfferedPackages" :content="creatorName" />
    </div>
    <div class="details">
      <NewTooltip v-if="!isMinified" class="title" :content="packageName" auto-ellipsis />
      <VideoComponentCategories :categories="categories" class="categories-component" :class="cSize.breakpointsAdditive" />
      <div class="meta" :class="{ 'meta-absolute': absoluteMeta }">
        <div class="stats" :class="{ 'small-gap': packageType === DataType.OfferedPackages }">
          <div v-for="(stat, index) in stats" :key="index" class="stats-item">
            <NewTooltip v-if="stat.tooltip" :content="stat.tooltip" placement="top">
              <IconWrapper :name="stat.icon" :icon-height="stat.iconHeight || 20" />
            </NewTooltip>
            <IconWrapper v-else :name="stat.icon" :icon-height="stat.iconHeight || 20" />
            <span>{{ stat.value }} </span>
          </div>
        </div>
        <div>
          <div v-if="packageType === DataType.Package" class="right-section">
            <IconWrapper name="timeIcon" :icon-height="18" />
            <span>{{ formatDateWithMonthName(new Date(createdDate)) }}</span>
          </div>
          <div v-else>
            <div v-if="offeredPackageStatus !== 'active'" class="right-section">
              <Button :variant="ButtonVariant.TERTIARY" :size="ButtonSize.SMALL" @click="emit('offered-package-decline')">
                {{ $t('decline') }}
              </Button>
              <Button :variant="ButtonVariant.SECONDARY" :size="ButtonSize.SMALL" @click="emit('offered-package-approval')">
                {{ $t('accept') }}
              </Button>
            </div>
            <Label v-else :text="$t('content-package-accepted')" :color="LabelColors.Green" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { ProfileImage, IconWrapper } from '@/components/common'
import NewTooltip from '@/components/common/tooltips/NewTooltip.vue'
import VideoComponentCategories from '@/components/base/video-component-tags/VideoComponentCategories.vue'
import ActionButtons, { Action } from '@/components/common/action-buttons/ActionButtons.vue'
import VideoLayoutOrientation from '@/components/base/video-component-tags/VideoLayoutOrientation.vue'
import { componentSize } from '@/composables'
import { DataType, DataViewType, Orientation } from '@/interfaces'
import { formatDateWithMonthName, isToday } from '@/services/utils'
import { Button, Label } from '@/components/common'
import { onCoverImageError } from '@/components/content/packages/packageCoversUtils'
import { ButtonSize, ButtonVariant } from '@/styles/types'
import { LabelColors } from '@/components/common/label/utils'
import { TagSizes } from '@/components/common/tag/utils'
import { formatTimeAgoShort } from '@/services/utils'

type Stat = {
  icon: string
  value: string
  iconHeight?: number
  tooltip?: string
}

const props = withDefaults(
  defineProps<{
    coverImage: string
    packageName?: string
    totalVideos?: number
    totalFolders?: number
    publishersCount?: number
    declineCount?: number
    createdDate: string
    profileImage?: string
    creatorName?: string
    categories?: string[]
    isMinified?: boolean
    dropdownActions?: Action[]
    offeredPackageStatus?: string
    fallbackProfileImage?: string
    absoluteMeta?: boolean
    packageType: DataType.OfferedPackages | DataType.Package
    orientations?: Orientation[]
  }>(),
  {
    categories: () => []
  }
)

const emit = defineEmits<{
  (e: 'offered-package-approval'): void
  (e: 'offered-package-decline'): void
}>()

const { t } = useI18n()
const cSize = reactive(componentSize())

function formatCreatedDateLabel(date: string): string {
  return isToday(date) ? t('today') : `${formatTimeAgoShort(date)} ${t('time-ago')}`
}

const stats = computed<Stat[]>(() => {
  const result: Stat[] = []
  if (props.packageType === DataType.Package) {
    result.push({ icon: 'contentIcon', value: String(props.totalVideos), tooltip: t('videos') })
    if (props.totalFolders !== undefined) {
      result.push({ icon: 'folderIcon', value: String(props.totalFolders), tooltip: t('folders') })
    }
    if (props.publishersCount !== undefined) {
      result.push({ icon: 'successIcon', value: String(props.publishersCount), tooltip: t('content-package-connected-publishers') })
    }
    result.push({ icon: 'declineNeutralIcon', value: String(props.declineCount ?? 0), tooltip: t('content-package-declines') })
  }
  if (props.packageType === DataType.OfferedPackages && props.offeredPackageStatus !== 'active') {
    result.push({
      icon: 'timeIcon',
      value: formatCreatedDateLabel(props.createdDate),
      iconHeight: 18
    })
  }

  return result
})
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

:deep(.actions-container) {
  padding: 0;
  background-color: $white;
  border-radius: 0 0 0 6px;
}

:deep(.inline-block) {
  margin: 0 !important;
  border: none !important;
}

.package-card {
  width: 405px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0.5rem;
  border: 1px solid $inputBorder;
  background-color: $white;
  border-radius: 6px;
  cursor: pointer;
  margin: 6px;

  &:hover {
    border: 1px solid $purple500;
    outline: 6px solid $purple100;
  }

  &.minified {
    max-width: 296px;
  }

  .dropdown-actions {
    position: absolute;
    top: 4px;
    right: 0;
  }

  .creator-wrapper {
    position: absolute;
    width: 100%;
    max-width: 100%;
    right: 0;
    top: 92px;
    left: 10px;
    display: flex;
    align-items: baseline;
    gap: 10px;
    color: $bodyText;
    left: 16px;

    &.minified {
      width: 180px;
      top: 64px;
    }
  }
  .creator-wrapper-title {
    margin-left: 64px;
    width: 220px;
  }

  .thumbnail-wrapper {
    position: relative;

    .orientation-tags {
      position: absolute;
      top: 8px;
      left: 8px;
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .thumbnail {
      width: 100%;
      height: 110px;
      object-fit: cover;
      margin-bottom: 12px;
      border-radius: 6px;

    &.with-avatar {
      margin-bottom: 4px;
    }

    &.minified {
      height: 80px;
      margin-bottom: 0;
    }
  }
}

  .categories-component {
    height: 24px;
  }

  .details {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .title {
    font-size: 18px;
    font-weight: 700;
    color: $bodyText;
    align-items: center;
    width: inherit;
  }

  .meta {
    font-size: 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.meta-absolute {
      position: absolute;
    }

    .stats {
      color: $secondaryTitleText;
      display: flex;
      gap: 1rem;
      justify-content: space-between;

      &.small-gap {
        gap: 0.5rem;
      }

      .stats-item {
        display: flex;
        gap: 0.25rem;
      }
    }

    .right-section {
      display: flex;
      gap: 0.25rem;
      color: $secondaryTitleText;
    }
  }
}
</style>
